import { type ComponentType } from 'react'
import { z } from 'zod'

/**
 * Blog post registry.
 *
 * Posts are MDX files in `app/blog-posts/`. They live outside `app/routes/` on
 * purpose: anything inside a `+` route folder is picked up by remix-flat-routes
 * and would become an accidental route.
 *
 * Frontmatter is validated here rather than trusted. A malformed post throws at
 * module load - i.e. at dev-server start, at build, and in CI - instead of
 * silently shipping a page with no description or a broken canonical URL.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

const FrontmatterSchema = z.object({
	title: z.string().min(1).max(120),
	description: z.string().min(50).max(200),
	slug: z
		.string()
		.regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug must be lowercase kebab-case'),
	publishedAt: z.string().regex(ISO_DATE, 'publishedAt must be YYYY-MM-DD'),
	updatedAt: z.string().regex(ISO_DATE).optional(),
	author: z.string().min(1),
	tags: z.array(z.string()).default([]),
	ogImage: z.string().startsWith('/').optional(),
	readingMinutes: z.number().int().positive().optional(),
	/** Keeps the post out of production entirely. Still visible in dev. */
	draft: z.boolean().default(false),
})

export type PostMeta = z.infer<typeof FrontmatterSchema>

export type Post = {
	meta: PostMeta
	Content: ComponentType<Record<string, unknown>>
}

type MdxModule = {
	frontmatter: unknown
	default: ComponentType<Record<string, unknown>>
}

const modules = import.meta.glob<MdxModule>('../blog-posts/*.mdx', {
	eager: true,
})

function buildRegistry(): Post[] {
	const posts: Post[] = []
	const seenSlugs = new Map<string, string>()

	for (const [path, mod] of Object.entries(modules)) {
		const parsed = FrontmatterSchema.safeParse(mod.frontmatter)
		if (!parsed.success) {
			throw new Error(
				`Invalid blog frontmatter in ${path}:\n${z.prettifyError(parsed.error)}`,
			)
		}
		const meta = parsed.data

		const duplicate = seenSlugs.get(meta.slug)
		if (duplicate) {
			throw new Error(
				`Duplicate blog slug "${meta.slug}" in ${path} (already used by ${duplicate})`,
			)
		}
		seenSlugs.set(meta.slug, path)

		posts.push({ meta, Content: mod.default })
	}

	// Newest first. ISO dates sort correctly as strings.
	return posts.sort((a, b) =>
		b.meta.publishedAt.localeCompare(a.meta.publishedAt),
	)
}

export const allPosts: Post[] = buildRegistry()

/**
 * Publishing is evaluated in UTC. A post dated 2026-09-01 therefore goes live at
 * 00:00 UTC on that date, which is 10:00 in Melbourne (UTC+10). This is
 * deliberate: UTC is stable across daylight saving and identical on every
 * server, so scheduling is predictable rather than dependent on host locale.
 */
export function toIsoDate(date: Date): string {
	return date.toISOString().slice(0, 10)
}

/** True when a post is live: not a draft, and its publish date has arrived. */
export function isPublished(meta: PostMeta, now: Date = new Date()): boolean {
	if (meta.draft) return false
	return meta.publishedAt <= toIsoDate(now)
}

/**
 * Posts that are live. Used for the sitemap and RSS feed, which must never
 * expose drafts or scheduled posts regardless of environment.
 *
 * Scheduling works without a redeploy: this filter runs per request on the
 * server, so a future-dated post appears on its own once the date arrives
 * (subject to the route's Cache-Control).
 */
export function getPublishedPosts(now: Date = new Date()): Post[] {
	return allPosts.filter((post) => isPublished(post.meta, now))
}

/**
 * Posts to show in the UI. Identical to `getPublishedPosts` in production; in
 * development drafts and scheduled posts are included so they can be previewed
 * locally. This is what lets unfinished posts be merged to main safely.
 */
export function getListablePosts(now: Date = new Date()): Post[] {
	if (import.meta.env.DEV) return allPosts
	return getPublishedPosts(now)
}

export function getPostBySlug(
	slug: string,
	now: Date = new Date(),
): Post | undefined {
	return getListablePosts(now).find((post) => post.meta.slug === slug)
}

/**
 * Look up a post regardless of visibility.
 *
 * For rendering only. The loader is the access gate - by the time a component
 * runs, the server has already decided the post may be shown. Re-applying the
 * date filter during render would consult the *browser's* clock, so a user
 * whose device is a few minutes slow (or badly misconfigured) would hydrate a
 * blank page over a post the server had correctly served.
 */
export function findPostBySlug(slug: string): Post | undefined {
	return allPosts.find((post) => post.meta.slug === slug)
}

/** Sitemap entries for every live post, plus the index itself. */
export function getBlogSitemapEntries(
	now: Date = new Date(),
): Array<{ path: string; lastmod: string; priority: number }> {
	// The /blog index itself lives in the sitemap's static PUBLIC_ROUTES list,
	// so it stays listed even when nothing is published yet.
	return getPublishedPosts(now).map((post) => ({
		path: `/blog/${post.meta.slug}`,
		lastmod: post.meta.updatedAt ?? post.meta.publishedAt,
		priority: 0.6,
	}))
}

export function formatPostDate(isoDate: string): string {
	return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('en-GB', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		timeZone: 'UTC',
	})
}
